import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { GeneratedReportInfo, ReportGenerationModel } from '../reports.model';
import { Inventory } from '../../inventory/inventory.model';
import { AppState } from '../../model/reducer';
import _ from 'lodash';
import { Store } from '@ngrx/store';
import { GenerateReport, GetGenerationOutputComplete } from '../../model/report/report.actions';
import { Observable, Subject } from 'rxjs';
import { TenantHelper } from '../../common/helpers/tenant.helper';
import { PluginClassEnum } from '../../plugins/plugin.enums';
import { ProjectSummaryReportGenerationModel } from '../../projects/project-summary/project-summary-report/project-summary-report.model';
import { ProjectService } from '../../projects/project.service';

@Component({
    selector: 'adm4-report-generation',
    template: `
    <div class='full-height-flex'>
      <div class='generation-container'>
        @if (reportType === PluginClassEnum.Report) {
          <div class='select-inventory'> <!-- inventory selection is shown only for Report interface -->
            <label>Inventory</label>
            <mat-form-field class='inventory-select'>
              <mat-select (selectionChange)="onInventoryKeyChange($event)"
                [disabled]='!hasInventories'
                [value]="selectedInventoryKey"
                [disableOptionCentering]='true'
                #inventorySelection
                (click)='focusDropdownInput()'>
                @if (inventorySelection.focused) {
                  <adm4-searchable-dropdown-input
                    [sourceItems]="inventories"
                    [placeholder]="'Please select an inventory...'"
                    [searchableFormatFn]='searchableInventoryFormatFn'
                    [focusTrigger]='searchableDropdownInputFocusTrigger$'
                  (filteredResult)="updateSearchResult($event)"></adm4-searchable-dropdown-input>
                }
                @for (inventory of filteredInventoryList; track inventory) {
                  <mat-option
                    [value]='inventory.inventoryKey'>{{inventory.inventoryKey | cropTenantFromKey}}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
        }
        @if (reportType === PluginClassEnum.Report) {
          <div class='generate'>
            <button class='admn4-button-ellipse-blue' [disabled]='!selectedInventoryKey' (click)='generateReport()'>Generate</button>
          </div>
        }
        @if (reportType === PluginClassEnum.ProjectReport) {
          <div class='generate'>
            <button class='admn4-button-ellipse-blue' (click)='generateReport()'>Generate</button>
          </div>
        }
        <!-- for both report types -->
        <adm4-report-generation-info
          [lastReportInfo]="lastReportInfo">
        </adm4-report-generation-info>
      </div>
      <div class='remaining-space-flex-content-wrapper'>
        <div class='remaining-space-flex-content'>
          @if (reportType === PluginClassEnum.Report) {
            <adm4-report-generation-output
              [inventoryKey]="selectedInventoryKey">
            </adm4-report-generation-output>
          }
    
          @if (reportType === PluginClassEnum.ProjectReport) {
            <adm4-project-report-generation-output
              >
            </adm4-project-report-generation-output>
          }
        </div>
      </div>
    </div>
    `,
    styleUrls: ['./report-generation.component.scss'],
    standalone: false
})
export class ReportGenerationComponent implements OnChanges {
  private projectService = inject(ProjectService);
  private store$ = inject<Store<AppState>>(Store);

  @Input() inventories: Inventory[];
  @Input() reportClassName: string;
  @Input() reportType: PluginClassEnum; // Report | ProjectReport
  @Input() projectKey: string;
  @Input() lastReportInfo: GeneratedReportInfo | null;

  selectedInventoryKey: string;
  filteredInventoryList: Inventory[];

  _searchableDropdownInputFocusTrigger$: Subject<void> = new Subject<void>();
  searchableDropdownInputFocusTrigger$: Observable<void> = this._searchableDropdownInputFocusTrigger$.asObservable();

  ngOnChanges(changes: SimpleChanges) {
    if (changes.lastReportInfo && this.lastReportInfo) {
      this.selectedInventoryKey = this.lastReportInfo.inventoryKey;
    } else if (changes.inventories && !_.isEmpty(this.inventories)) {
      this.selectedInventoryKey = this.inventories[0].inventoryKey;
      this.updateSearchResult(this.inventories);
    }
  }

  onInventoryKeyChange(event) {
    this.selectedInventoryKey = event.value;
  }

  generateReport() {
    if (this.reportType === PluginClassEnum.Report) {
      const generationModel: ReportGenerationModel = {
        projectKey: this.projectKey,
        inventoryKey: this.selectedInventoryKey,
        reportClassName: this.reportClassName,
        mediaType: 'text/html'
      };
      this.store$.dispatch(new GenerateReport(generationModel));
    } else if (this.reportType === PluginClassEnum.ProjectReport) {
      const generationModel: ProjectSummaryReportGenerationModel = {
        reportClassName: this.reportClassName,
        // TODO koenig 20251002: for now we support only text/html here
        mediaType: 'text/html'
      };
      this.projectService.getProjectReport(this.projectKey, generationModel)
        .subscribe({
          next: (response) => {
            this.store$.dispatch(new GetGenerationOutputComplete({
              reportKey: this.projectKey.concat(this.reportClassName),
              // TODO koenig 20251002: for now we support only text/html here
              generationOutput: response
            }));
          },
          error: (err) => {
            console.error('Failed to generate project report', err);
          }
        });
    }
  }

  get hasInventories(): boolean {
    return !_.isEmpty(this.inventories);
  }

  searchableInventoryFormatFn = (inventory: Inventory): string => {
    return TenantHelper.cropTenantFromKey(inventory.inventoryKey);
  };

  updateSearchResult(filteredList: Inventory[]): void {
    this.filteredInventoryList = filteredList;
  }

  focusDropdownInput(): void {
    this._searchableDropdownInputFocusTrigger$.next();
  }

  protected readonly PluginClassEnum = PluginClassEnum;
}
